import {
  Box,
  Text,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  IconButton,
  HStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useState, useCallback } from 'react'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'

const ZOOM_MIN = 1
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

export function ManualScreenshot({
  src,
  alt,
  caption,
  border,
  textSecondary,
}: {
  src: string
  alt: string
  caption: string
  border: string
  textSecondary: string
}) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [zoom, setZoom] = useState(1)

  const handleClose = useCallback(() => {
    setZoom(1)
    onClose()
  }, [onClose])

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))
  }, [])

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))
  }, [])

  const resetZoom = useCallback(() => setZoom(1), [])

  return (
    <>
      <Box
        as="button"
        type="button"
        onClick={onOpen}
        borderWidth="1px"
        borderColor={border}
        borderRadius="md"
        overflow="hidden"
        bg="white"
        _dark={{ bg: 'gray.800' }}
        position={{ lg: 'sticky' }}
        top={{ lg: 4 }}
        w="full"
        textAlign="left"
        cursor="zoom-in"
        transition="box-shadow 0.15s ease, border-color 0.15s ease"
        _hover={{
          borderColor: 'blue.400',
          boxShadow: 'md',
        }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'blue.400',
          outlineOffset: '2px',
        }}
        aria-label={`Enlarge screenshot: ${alt}`}
      >
        <Box position="relative">
          <Image
            src={src}
            alt={alt}
            w="100%"
            maxH={{ base: '320px', lg: '520px' }}
            objectFit="contain"
            bg="gray.50"
            _dark={{ bg: 'gray.900' }}
            pointerEvents="none"
          />
          <HStack
            position="absolute"
            bottom={2}
            right={2}
            spacing={1}
            bg="blackAlpha.700"
            color="white"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            pointerEvents="none"
          >
            <ZoomIn size={14} />
            <Text as="span">Click to zoom</Text>
          </HStack>
        </Box>
        <Text fontSize="sm" color={textSecondary} px={3} py={2} borderTopWidth="1px" borderColor={border}>
          {caption}
        </Text>
      </Box>

      <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent
          bg="gray.900"
          color="white"
          maxW={{ base: '100vw', md: '95vw' }}
          maxH={{ base: '100vh', md: '95vh' }}
          m={{ base: 0, md: 4 }}
          borderRadius={{ base: 0, md: 'md' }}
        >
          <ModalCloseButton zIndex={2} />
          <HStack
            px={4}
            pt={4}
            pb={2}
            pr={12}
            spacing={2}
            flexWrap="wrap"
            borderBottomWidth="1px"
            borderColor="whiteAlpha.200"
          >
            <Text fontSize="sm" flex="1" minW="120px" noOfLines={2}>
              {caption}
            </Text>
            <IconButton
              aria-label="Zoom out"
              icon={<ZoomOut size={18} />}
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={zoomOut}
              isDisabled={zoom <= ZOOM_MIN}
            />
            <Text fontSize="sm" minW="48px" textAlign="center" fontFamily="mono">
              {Math.round(zoom * 100)}%
            </Text>
            <IconButton
              aria-label="Zoom in"
              icon={<ZoomIn size={18} />}
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={zoomIn}
              isDisabled={zoom >= ZOOM_MAX}
            />
            <IconButton
              aria-label="Reset zoom"
              icon={<Maximize2 size={18} />}
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={resetZoom}
              isDisabled={zoom === 1}
            />
          </HStack>
          <ModalBody
            p={4}
            overflow="auto"
            onWheel={(e) => {
              if (!e.ctrlKey && !e.metaKey) return
              e.preventDefault()
              if (e.deltaY < 0) zoomIn()
              else zoomOut()
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="flex-start"
              minH="60vh"
              cursor={zoom > 1 ? 'zoom-out' : 'zoom-in'}
              onClick={() => (zoom >= ZOOM_MAX ? resetZoom() : zoomIn())}
            >
              <Image
                src={src}
                alt={alt}
                maxW="100%"
                objectFit="contain"
                transform={`scale(${zoom})`}
                transformOrigin="top center"
                transition="transform 0.15s ease"
                userSelect="none"
                draggable={false}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
